import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, Trash, PencilSimple, Plus, ArrowSquareOut } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import usePageMeta from "@/lib/usePageMeta";
import {
  adminLeads, adminUpdateLead, adminDeleteLead,
  adminCases, adminCreateCase, adminUpdateCase, adminDeleteCase,
  getContent, adminUpdateContent,
} from "@/lib/api";

const LEAD_STATUS = ["ny", "kontaktet", "vundet", "lukket"];
const EMPTY_CASE = { slug: "", title: "", subtitle: "", location: "", year: "", category: "Vinduer & Døre", summary: "", description: "", cover_image: "", images: [], materials: [], published: true, order: 0 };

function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const load = () => adminLeads().then(setLeads).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await adminUpdateLead(id, status);
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    toast.success("Status opdateret");
  };
  const remove = async (id) => {
    await adminDeleteLead(id);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    toast.success("Lead slettet");
  };

  return (
    <div data-testid="admin-leads-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl font-light">Leads <span className="text-muted-foreground text-xl">({leads.length})</span></h2>
      </div>
      {leads.length === 0 ? (
        <p className="text-muted-foreground font-light py-12 text-center border border-dashed border-border">Ingen leads endnu.</p>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} data-testid={`lead-row-${l.id}`} className="border border-border p-5 grid md:grid-cols-12 gap-4 items-start bg-card">
              <div className="md:col-span-3">
                <p className="font-medium">{l.name}</p>
                <a href={`tel:${l.phone}`} className="text-sm text-muted-foreground block hover:text-foreground">{l.phone}</a>
                <a href={`mailto:${l.email}`} className="text-sm text-muted-foreground block hover:text-foreground break-all">{l.email}</a>
              </div>
              <div className="md:col-span-5">
                <p className="text-sm font-light text-foreground/80">{l.message || "—"}</p>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{l.source}</Badge>
                  {l.page && <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{l.page}</Badge>}
                </div>
              </div>
              <div className="md:col-span-2 text-xs text-muted-foreground">
                {l.created_at ? new Date(l.created_at).toLocaleString("da-DK") : ""}
              </div>
              <div className="md:col-span-2 flex items-center gap-2 justify-end">
                <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                  <SelectTrigger className="w-32 h-9 rounded-none" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="rounded-none" onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`}>
                  <Trash size={18} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CaseEditor({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_CASE);
  useEffect(() => { setForm(initial ? { ...EMPTY_CASE, ...initial } : EMPTY_CASE); }, [initial, open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.slug || !form.title || !form.cover_image) {
      toast.error("Udfyld mindst slug, titel og forsidebillede.");
      return;
    }
    const payload = {
      ...form,
      order: Number(form.order) || 0,
      images: Array.isArray(form.images) ? form.images : String(form.images).split("\n").map((s) => s.trim()).filter(Boolean),
      materials: Array.isArray(form.materials) ? form.materials : String(form.materials).split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (initial?.id) await adminUpdateCase(initial.id, payload);
      else await adminCreateCase(payload);
      toast.success("Case gemt");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Kunne ikke gemme (slug skal være unikt).");
    }
  };

  const imagesText = Array.isArray(form.images) ? form.images.join("\n") : form.images;
  const materialsText = Array.isArray(form.materials) ? form.materials.join("\n") : form.materials;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="case-editor">
        <DialogHeader><DialogTitle className="font-display text-3xl font-light">{initial?.id ? "Rediger case" : "Ny case"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Titel</Label><Input className="rounded-none mt-1" value={form.title} onChange={set("title")} data-testid="case-title" /></div>
          <div><Label>Slug</Label><Input className="rounded-none mt-1" value={form.slug} onChange={set("slug")} data-testid="case-slug" /></div>
          <div className="col-span-2"><Label>Underoverskrift</Label><Input className="rounded-none mt-1" value={form.subtitle} onChange={set("subtitle")} /></div>
          <div><Label>Lokation</Label><Input className="rounded-none mt-1" value={form.location} onChange={set("location")} /></div>
          <div><Label>År</Label><Input className="rounded-none mt-1" value={form.year} onChange={set("year")} /></div>
          <div><Label>Kategori</Label><Input className="rounded-none mt-1" value={form.category} onChange={set("category")} /></div>
          <div><Label>Rækkefølge</Label><Input type="number" className="rounded-none mt-1" value={form.order} onChange={set("order")} /></div>
          <div className="col-span-2"><Label>Forsidebillede (sti)</Label><Input className="rounded-none mt-1" value={form.cover_image} onChange={set("cover_image")} placeholder="/images/…" /></div>
          <div className="col-span-2"><Label>Resumé</Label><Textarea className="rounded-none mt-1" rows={2} value={form.summary} onChange={set("summary")} /></div>
          <div className="col-span-2"><Label>Beskrivelse</Label><Textarea className="rounded-none mt-1" rows={4} value={form.description} onChange={set("description")} /></div>
          <div className="col-span-2"><Label>Billeder (én sti pr. linje)</Label><Textarea className="rounded-none mt-1 font-mono text-xs" rows={4} value={imagesText} onChange={(e) => setForm({ ...form, images: e.target.value.split("\n") })} /></div>
          <div className="col-span-2"><Label>Materialer (ét pr. linje)</Label><Textarea className="rounded-none mt-1" rows={3} value={materialsText} onChange={(e) => setForm({ ...form, materials: e.target.value.split("\n") })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Annuller</Button>
          <Button className="rounded-none" onClick={save} data-testid="case-save">Gem</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CasesPanel() {
  const [cases, setCases] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => adminCases().then(setCases).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await adminDeleteCase(id);
    setCases((c) => c.filter((x) => x.id !== id));
    toast.success("Case slettet");
  };

  return (
    <div data-testid="admin-cases-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl font-light">Cases <span className="text-muted-foreground text-xl">({cases.length})</span></h2>
        <Button className="rounded-none" onClick={() => { setEditing(null); setOpen(true); }} data-testid="case-new"><Plus size={16} className="mr-2" /> Ny case</Button>
      </div>
      <div className="space-y-3">
        {cases.map((c) => (
          <div key={c.id} data-testid={`case-row-${c.slug}`} className="border border-border p-4 flex items-center gap-4 bg-card">
            <div className="w-20 h-16 overflow-hidden bg-muted shrink-0"><img src={c.cover_image} alt="" className="img-cover" /></div>
            <div className="flex-1">
              <p className="font-medium">{c.title}</p>
              <p className="text-sm text-muted-foreground">/{c.slug} · {c.location} · {c.year}</p>
            </div>
            <a href={`/cases/${c.slug}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground"><ArrowSquareOut size={18} /></a>
            <Button variant="ghost" size="icon" className="rounded-none" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`case-edit-${c.slug}`}><PencilSimple size={18} /></Button>
            <Button variant="ghost" size="icon" className="rounded-none" onClick={() => remove(c.id)} data-testid={`case-delete-${c.slug}`}><Trash size={18} /></Button>
          </div>
        ))}
      </div>
      <CaseEditor open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}

function ContentPanel() {
  const [hero, setHero] = useState({});
  const [contact, setContact] = useState({});
  useEffect(() => {
    getContent().then((d) => { setHero(d.hero || {}); setContact(d.contact || {}); }).catch(() => {});
  }, []);
  const save = async (key, value) => {
    await adminUpdateContent(key, value);
    toast.success("Indhold gemt");
  };
  return (
    <div className="grid md:grid-cols-2 gap-10" data-testid="admin-content-panel">
      <div>
        <h2 className="font-display text-3xl font-light mb-6">Hero</h2>
        <div className="space-y-4">
          {["eyebrow", "line1", "line2", "line3", "cta"].map((k) => (
            <div key={k}><Label className="capitalize">{k}</Label><Input className="rounded-none mt-1" value={hero[k] || ""} onChange={(e) => setHero({ ...hero, [k]: e.target.value })} data-testid={`hero-${k}`} /></div>
          ))}
          <div><Label>Intro</Label><Textarea className="rounded-none mt-1" rows={3} value={hero.intro || ""} onChange={(e) => setHero({ ...hero, intro: e.target.value })} /></div>
          <Button className="rounded-none" onClick={() => save("hero", hero)} data-testid="save-hero">Gem hero</Button>
        </div>
      </div>
      <div>
        <h2 className="font-display text-3xl font-light mb-6">Kontaktoplysninger</h2>
        <div className="space-y-4">
          {["phone", "email", "address", "cvr"].map((k) => (
            <div key={k}><Label className="capitalize">{k}</Label><Input className="rounded-none mt-1" value={contact[k] || ""} onChange={(e) => setContact({ ...contact, [k]: e.target.value })} data-testid={`contact-${k}`} /></div>
          ))}
          <Button className="rounded-none" onClick={() => save("contact", contact)} data-testid="save-contact">Gem kontakt</Button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  usePageMeta({ title: "Dashboard", noindex: true });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur z-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <span className="font-display text-2xl tracking-[0.25em]">HAGELUND</span>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.email}</span>
            <a href="/" target="_blank" rel="noreferrer" className="text-xs uppercase tracking-label hover:text-accent hidden sm:block">Se site</a>
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout">
              <SignOut size={16} className="mr-2" /> Log ud
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        <Tabs defaultValue="leads">
          <TabsList className="rounded-none bg-muted mb-8">
            <TabsTrigger value="leads" className="rounded-none" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="cases" className="rounded-none" data-testid="tab-cases">Cases</TabsTrigger>
            <TabsTrigger value="content" className="rounded-none" data-testid="tab-content">Indhold</TabsTrigger>
          </TabsList>
          <TabsContent value="leads"><LeadsPanel /></TabsContent>
          <TabsContent value="cases"><CasesPanel /></TabsContent>
          <TabsContent value="content"><ContentPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
